import React, { useCallback, useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

const useIntervalInformationStyles = makeStyles(theme => ({
  intervalContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  intervalItemWrapper: {
    display: 'flex',
  },
  intervalColorWrapper: {
    height: '1.35rem',
    display: 'flex',
    alignItems: 'center',
  },
  intervalColor: {
    height: 12,
    width: 12,
    marginRight: 8,
    borderRadius: 6,
  },
  actualIntervalColor: {
    height: 14,
    width: 14,
    marginRight: 8,
    borderRadius: 7,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#FFFFFF',
  },
  intervalValues: {
    ...theme.typography.subtitle1,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  intervalLabel: {
    fontSize: '0.9rem',
    color: theme.palette.text.primary,
  },
}));

const useIntervalInformation = ({ intervals = [] }) => {
  const classes = useIntervalInformationStyles();

  const sortedIntervals = useMemo(
    () => intervals.sort((a, b) => a.start - b.start),
    [intervals],
  );

  const numIntervals = sortedIntervals.length;
  const intervalsCol = useMemo(() => {
    let result;
    switch (numIntervals) {
      case 1:
        result = 12;
        break;
      case 2:
        result = 6;
        break;
      case 3:
        result = 4;
        break;
      case 4:
        result = 3;
        break;
      default:
        result = 3;
        break;
    }

    return result;
  }, [numIntervals]);

  const parsedIntervalsDisplay = useMemo(
    () =>
      sortedIntervals.map(interval => {
        const { start, end } = interval;

        return {
          start,
          end,
          color: interval.color,
          label: interval.label,
        };
      }),
    [sortedIntervals],
  );

  const renderIntervalInformation = useCallback(() => {
    if (numIntervals <= 0) {
      return null;
    }

    return (
      <Grid container spacing={2} className={classes.intervalContainer}>
        {parsedIntervalsDisplay.map(interval => (
          <Grid
            item
            xs={6}
            md={intervalsCol}
            className={classes.intervalItemWrapper}
            key={`overall-component-interval-${interval.color}`}
          >
            <Box>
              <Box className={classes.intervalColorWrapper}>
                <span
                  className={classes.intervalColor}
                  style={{
                    backgroundColor: interval.color,
                  }}
                />
              </Box>
            </Box>
            <Box className={classes.intervalInfoWrapper}>
              <Typography
                className={classes.intervalValues}
              >{`${interval.start}-${interval.end}`}</Typography>
              <Typography className={classes.intervalLabel}>
                {interval.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }, [classes, intervalsCol, numIntervals, parsedIntervalsDisplay]);

  return {
    sortedIntervals,
    intervalsCol,
    parsedIntervalsDisplay,
    renderIntervalInformation,
  };
};

export default useIntervalInformation;
